import { queryClient } from '@/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  type CreateTask,
  type Task,
  TaskSchema,
  type Tasks,
  type TasksQueryOptions,
  TasksSchema,
  type UpdateTask,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const TASKS_QUERY_KEY = 'tasks';

export const useTaskQuery = (taskId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['task', taskId],
    queryFn: async () => {
      const response = await apiClient.get<Task>(`/tasks/${taskId}`);
      const validatedData = TaskSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn({ taskId }, 'failed to validate task data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useTasksQuery = (
  options: TasksQueryOptions,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [TASKS_QUERY_KEY, options],
    queryFn: async () => {
      const response = await apiClient.get<Tasks>('/tasks', {
        params: options,
      });
      const validatedData = TasksSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn({}, 'failed to validate tasks data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const createTask = async (task: CreateTask) => {
  const response = await apiClient.post<Task>('/tasks', task);
  const validatedData = TaskSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn({}, 'failed to validate task data', {
      error: validatedData.error,
    });
    return response.data;
  }
  return validatedData.data;
};

export const useCreateTaskMutation = () => {
  return useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [TASKS_QUERY_KEY],
      });
    },
  });
};

/**
 * Create a task and optionally start it immediately
 */
export const createAndStartTask = async (
  task: CreateTask & { shouldStart?: boolean },
) => {
  const { shouldStart, ...taskData } = task;
  const response = await apiClient.post<Task>('/tasks', {
    ...taskData,
    shouldStart: shouldStart ?? false,
  });
  const validatedData = TaskSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn(
      { taskId: response.data?.taskId },
      'failed to validate task data',
      {
        error: validatedData.error,
      },
    );
    return response.data;
  }
  return validatedData.data;
};

/**
 * Mutation hook for creating a task with auto-start capability
 */
export const useCreateAndStartTaskMutation = () => {
  return useMutation({
    mutationFn: createAndStartTask,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [TASKS_QUERY_KEY],
      });
      if (data.taskId) {
        void queryClient.invalidateQueries({
          queryKey: ['task', data.taskId],
        });
      }
    },
  });
};

/**
 * Mutation hook for creating a new empty task from navigation
 */
export const useCreateNewTaskMutation = () => {
  return useMutation({
    mutationFn: createTask,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: [TASKS_QUERY_KEY],
      });
      if (data.taskId) {
        void queryClient.invalidateQueries({
          queryKey: ['task', data.taskId],
        });
      }
    },
  });
};

/**
 * Update task details (name, description, metadata)
 */
export const updateTaskDetails = async (
  taskId: string,
  details: UpdateTask,
) => {
  const response = await apiClient.patch<Task>(`/tasks/${taskId}`, details);
  const validatedData = TaskSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn({ taskId }, 'failed to validate task state data after update', {
      error: validatedData.error,
    });
    return response.data;
  }
  return validatedData.data;
};

/**
 * Mutation hook for updating task details
 */
export const useUpdateTaskDetailsMutation = (taskId: string) => {
  return useMutation({
    mutationFn: (details: UpdateTask) => updateTaskDetails(taskId, details),
    onSuccess: () => {
      // Invalidate both the task and its state
      void queryClient.invalidateQueries({
        queryKey: [TASKS_QUERY_KEY, { taskId }],
      });
      // Also invalidate the tasks list
      void queryClient.invalidateQueries({
        queryKey: [TASKS_QUERY_KEY],
      });
    },
  });
};
